import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { Transaction, Account, Category } from '../types';
import CategorySelect from '../components/CategorySelect';
import { getCategoryDisplayName, getAccountTypeDisplayName } from '../services/categoryService';
import { Analytics } from '../utils/analytics';

const Subscriptions: React.FC = () => {
  const [subscriptions, setSubscriptions] = useState<Transaction[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const isMobile = window.innerWidth <= 768;
  const [formData, setFormData] = useState({
    accountId: '',
    amount: '',
    category: 'UTILITIES',
    customCategory: '',
    description: '',
    date: new Date().toISOString().split('T')[0],
    autoBook: false,
    bookingDay: new Date().getDate().toString()
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [transRes, accRes, catRes] = await Promise.all([
        api.get('/transactions'),
        api.get('/accounts'),
        api.get('/categories')
      ]);
      // Nur wiederkehrende Transaktionen (Abos)
      const recurring = transRes.data.data.transactions.filter(
        (t: Transaction) => t.isRecurring
      );
      setSubscriptions(recurring);
      setAccounts(accRes.data.data.accounts);
      setCategories(catRes.data.data.categories);
    } catch (error) {
      console.error('Fehler beim Laden:', error);
    } finally {
      setLoading(false);
    }
  };

  const getDisplayNameForSubscription = (subscription: Transaction) => {
    const customCategoryName = (subscription as any).customCategory;
    const customCategories = categories
      .filter(cat => cat.isCustom)
      .map(cat => ({ name: cat.name, displayName: cat.displayName }));

    return getCategoryDisplayName(customCategoryName || subscription.category, customCategories);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const payload: any = {
        ...formData,
        isRecurring: true // Immer als wiederkehrend markieren
      };

      if (!payload.customCategory) {
        delete payload.customCategory;
      }

      if (editingId) {
        await api.put(`/transactions/${editingId}`, payload);
      } else {
        await api.post('/transactions', payload);
        Analytics.subscriptionCreated(formData.customCategory || formData.category, parseFloat(formData.amount));
      }

      setShowForm(false);
      setEditingId(null);
      setFormData({
        accountId: '',
        amount: '',
        category: 'UTILITIES',
        customCategory: '',
        description: '',
        date: new Date().toISOString().split('T')[0],
        autoBook: false,
        bookingDay: new Date().getDate().toString()
      });
      fetchData();
    } catch (error) {
      console.error('Fehler beim Speichern:', error);
    }
  };

  const handleEdit = (subscription: Transaction) => {
    setEditingId(subscription.id);
    setFormData({
      accountId: subscription.accountId,
      amount: subscription.amount.toString(),
      category: (subscription as any).customCategory || subscription.category,
      customCategory: (subscription as any).customCategory || '',
      description: subscription.description,
      date: subscription.date.split('T')[0],
      autoBook: (subscription as any).autoBook || false,
      bookingDay: ((subscription as any).bookingDay || new Date().getDate()).toString()
    });
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Abo wirklich löschen?')) {
      try {
        await api.delete(`/transactions/${id}`);
        Analytics.subscriptionDeleted();
        fetchData();
      } catch (error) {
        console.error('Fehler beim Löschen:', error);
      }
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingId(null);
    setFormData({
      accountId: '',
      amount: '',
      category: 'UTILITIES',
      customCategory: '',
      description: '',
      date: new Date().toISOString().split('T')[0],
      autoBook: false,
      bookingDay: ''
    });
  };

  if (loading) {
    return <div className="loading">Lädt...</div>;
  }

  return (
    <div className="container">
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        flexWrap: 'wrap',
        marginBottom: '20px' 
      }}>
        <h1 style={{ 
          minWidth: isMobile ? '100%' : 'auto',
          marginBottom: isMobile ? '10px' : '0'
        }}>
          Abos & wiederkehrende Zahlungen
        </h1>
        <button 
          className="btn btn-primary" 
          onClick={() => setShowForm(!showForm)}
          style={{
            width: isMobile ? '100%' : 'auto',
            padding: '8px 16px'
          }}
        >
          {showForm ? 'Abbrechen' : '+ Neues Abo'}
        </button>
      </div>

      {showForm && (
        <div className="card">
          <h2>{editingId ? 'Abo bearbeiten' : 'Neues Abo erstellen'}</h2>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Konto *</label>
              <select
                value={formData.accountId}
                onChange={(e) => setFormData({ ...formData, accountId: e.target.value })}
                required
              >
                <option value="">Bitte wählen...</option>
                {accounts.map((acc) => (
                  <option key={acc.id} value={acc.id}>
                    {acc.name} ({getAccountTypeDisplayName(acc.type)})
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Kategorie *</label>
              <CategorySelect
                value={formData.category}
                onChange={(category, isCustom) => setFormData({
                  ...formData,
                  category,
                  customCategory: isCustom ? category : ''
                })}
                required
              />
            </div>
            <div className="form-group">
              <label>Betrag * (negativ für Ausgabe)</label>
              <input
                type="number"
                step="0.01"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                required
                placeholder="-12.99"
              />
            </div>
            <div className="form-group">
              <label>Beschreibung * (z.B. Netflix, Spotify)</label>
              <input
                type="text"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                required
                placeholder="Netflix Premium"
              />
            </div>
            <div className="form-group">
              <label>Startdatum *</label>
              <input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>Buchungstag im Monat (1-31) *</label>
              <input
                type="number"
                min="1"
                max="31"
                value={formData.bookingDay}
                onChange={(e) => setFormData({ ...formData, bookingDay: e.target.value })}
                required
                placeholder="z.B. 1 für jeden 1. des Monats"
              />
              <small style={{ color: '#666', display: 'block', marginTop: '5px' }}>
                An diesem Tag wird das Abo monatlich fällig
              </small>
            </div>
            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <input
                  type="checkbox"
                  checked={formData.autoBook}
                  onChange={(e) => setFormData({ ...formData, autoBook: e.target.checked })}
                  style={{ width: 'auto', margin: 0 }}
                />
                <span>Automatisch buchen</span>
              </label>
              <small style={{ color: '#666', display: 'block', marginTop: '5px' }}>
                {formData.autoBook 
                  ? '✓ Wird automatisch am Buchungstag gebucht' 
                  : '○ Muss manuell im Dashboard bestätigt werden'}
              </small>
            </div>
            <div style={{ 
              display: 'flex', 
              gap: '10px',
              flexDirection: isMobile ? 'column' : 'row'
            }}>
              <button 
                type="submit" 
                className="btn btn-success"
                style={{
                  width: isMobile ? '100%' : 'auto',
                  padding: '12px 24px'
                }}
              >
                {editingId ? 'Änderungen speichern' : 'Abo erstellen'}
              </button>
              {showForm && (
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={handleCancel}
                  style={{
                    width: isMobile ? '100%' : 'auto',
                    padding: '12px 24px'
                  }}
                >
                  Abbrechen
                </button>
              )}
            </div>
          </form>
        </div>
      )}

      <div className="card" style={{ marginTop: '20px' }}>
        <h2>Meine Abos ({subscriptions.length})</h2>
        {subscriptions.length === 0 ? (
          <p style={{ color: '#666', marginTop: '10px' }}>Keine Abos vorhanden.</p>
        ) : (
          <table style={{ marginTop: '10px' }}>
            <thead>
              <tr>
                <th>Beschreibung</th>
                <th>Kategorie</th>
                <th>Konto</th>
                <th>Betrag</th>
                <th>Buchungstag</th>
                <th>Modus</th>
                <th>Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {subscriptions.map((subscription) => {
                const amount = Math.abs(parseFloat(subscription.amount.toString()));
                
                return (
                  <tr key={subscription.id}>
                    <td data-label="Beschreibung"><strong>{subscription.description}</strong></td>
                    <td data-label="Kategorie">
                      <span className="badge badge-info">{getDisplayNameForSubscription(subscription)}</span>
                    </td>
                    <td data-label="Konto">{subscription.account?.name}</td>
                    <td data-label="Betrag" style={{ color: '#dc3545', fontWeight: 'bold' }}>
                      {amount.toFixed(2)} € / Monat
                    </td>
                    <td data-label="Buchungstag">Jeden {(subscription as any).bookingDay || new Date(subscription.date).getDate()}. des Monats</td>
                    <td data-label="Modus">
                      {(subscription as any).autoBook ? (
                        <span className="badge badge-success">Automatisch</span>
                      ) : (
                        <span className="badge badge-warning">Manuell</span>
                      )}
                    </td>
                    <td data-label="Aktionen">
                      <div style={{ 
                        display: 'flex', 
                        gap: '5px',
                        flexDirection: isMobile ? 'column' : 'row'
                      }}>
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => handleEdit(subscription)}
                          style={{
                            width: isMobile ? '100%' : 'auto'
                          }}
                        >
                          Bearbeiten
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => handleDelete(subscription.id)}
                          style={{
                            width: isMobile ? '100%' : 'auto'
                          }}
                        >
                          Löschen
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {subscriptions.length > 0 && (
        <div className="card" style={{ marginTop: '20px', backgroundColor: '#f8f9fa' }}>
          <h3 style={{ fontSize: '18px', marginBottom: '10px' }}>💡 Monatliche Gesamtkosten</h3>
          <p style={{ fontSize: '24px', fontWeight: 'bold', color: '#dc3545' }}>
            {subscriptions.reduce((sum, sub) => sum + Math.abs(parseFloat(sub.amount.toString())), 0).toFixed(2)} €
          </p>
        </div>
      )}
    </div>
  );
};

export default Subscriptions;
